import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  IApplication,
  IApplicationListItem,
  IState,
} from '../interfaces/account/account.interface';
import {
  getApplication,
  getApplications,
  getApplicationsForUser,
  getApplicationsWithProviders,
  getBillingTypes,
  getFormatTypes,
  getStates,
} from '../interfaces/common.graphql';
import { IbillingIdTypes } from '../interfaces/common.interface';
import {
  GQLArguments,
  GQLResponse,
  IGQLResponse,
  IGQLSingleResponse,
} from '../interfaces/graphql.interface';
import { DEFAULT_PAGE_SIZE } from '../variables/variables';
import { HelperGQLService } from './helper.graphql.service';

@Injectable({
  providedIn: 'root',
})
export class CommonGraphqlService {
  constructor(
    private apollo: Apollo,
    private gqlService: HelperGQLService,
  ) {}

  ngbDate(d?: Date) {
    return d
      ? { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() }
      : undefined;
  }

  getApplications(
    params?: ApplicationSearchParams,
    codes?: string[],
  ): Observable<IApplication[]> {
    const gqlArguments: GQLArguments = {
      first: 200,
    };

    if (params) {
      const claimsFilters: Record<string, unknown> = {
        description: { contains: params.name },
        or: codes
          ? {
              or: codes.map((x) => {
                return { eq: x };
              }),
            }
          : null,
      };
      gqlArguments.where = this.gqlService.cleanFilterObject(claimsFilters);
    }

    return this.apollo
      .query<IGQLResponse<IApplication>>({
        query: getApplications,
        variables: {
          ...gqlArguments,
        },
      })
      .pipe(
        map((response) => {
          return response.data.applications.nodes;
        }),
      );
  }

  getApplication(code: string): Observable<IApplication> {
    //account.pMs
    return this.apollo
      .query<IGQLSingleResponse<IApplication>>({
        query: getApplication,
        fetchPolicy: 'no-cache',
        variables: {
          code,
        },
      })
      .pipe(
        map((response) => {
          return response.data.application;
        }),
      );
  }

  getApplicationsForUser(
    params?: ApplicationSearchParams,
  ): Observable<GQLResponse<IApplicationListItem>> {
    const gqlArguments: GQLArguments = { first: DEFAULT_PAGE_SIZE };

    if (params) {
      gqlArguments.where = { description: { contains: params.name } };
    }
    if (params?.cursor) {
      gqlArguments.after = params.cursor;
    }

    gqlArguments.order = { description: 'ASC' };

    return this.apollo
      .query<IGQLResponse<IApplicationListItem>>({
        query: getApplicationsForUser,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .pipe(
        map((response) => {
          return response.data.applications;
        }),
      );
  }

  getApplicationsWithProviders(
    params?: ApplicationSearchParams,
  ): Observable<IApplication[]> {
    const gqlArguments: GQLArguments = {
      first: 200,
    };

    if (params) {
      gqlArguments.where = { description: { contains: params.name } };
    }

    return this.apollo
      .watchQuery<IGQLResponse<IApplication>>({
        query: getApplicationsWithProviders,
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.applications.nodes;
        }),
      );
  }

  getStates(): Observable<IState[]> {
    return this.apollo
      .query<IGQLResponse<IState>>({
        query: getStates,
        fetchPolicy: 'cache-first',
      })
      .pipe(
        map((response) => {
          return response.data.states.nodes;
        }),
      );
  }

  getBillingTypes(): Observable<IbillingIdTypes[]> {
    return this.apollo
      .watchQuery<IGQLSingleResponse<IbillingIdTypes[]>>({
        query: getBillingTypes,
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'ignore',
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.billingIdTypes;
        }),
      );
  }

  getFormatTypes(): Observable<IFormatType[]> {
    return this.apollo
      .watchQuery<IGQLResponse<IFormatType>>({
        query: getFormatTypes,
        errorPolicy: 'ignore',
        variables: {
          first: DEFAULT_PAGE_SIZE,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.formatTypes.nodes;
        }),
      );
  }
}

export type ApplicationSearchParams = {
  clientId?: string;
  codes?: string[];
  cursor?: string;
  groupId?: string;
  name?: string;
  taxId?: string;
};

export type IFormatType = {
  description: string;
  id: string;
};
